import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { MdContentCopy, MdNotificationsActive } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import TableColumn from "../../components/TableColumn/TableColumn";
import api from "../../services/api";

// Same copy cell as the events list: shows a short id, copies the full one for Manual Pushes.
const PlanIdCell = ({ planId }) => {
  if (!planId) return "—";
  const short = planId.length > 12 ? `${planId.slice(0, 8)}…${planId.slice(-4)}` : planId;
  return (
    <span
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(planId);
        toast.success("Plan ID copied");
      }}
      title={`${planId}\n(click to copy)`}
      style={{
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 12,
        color: "rgba(255,255,255,0.6)",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {short}
      <MdContentCopy size={14} />
    </span>
  );
};

// "Private" is the app's "Make this plan private" (unlisted); approval plans are still public.
const VISIBILITY_LABELS = {
  public: "Public",
  private: "Public, approval required",
  unlisted: "Private (link only)",
};

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "", label: "All but deleted" },
  { value: "removed", label: "Removed" },
  { value: "deleted", label: "Deleted" },
];

const formatWhen = (ms) => (ms ? format(new Date(Number(ms)), "EEE MMM d, yyyy h:mm a") : "—");

// "Starts", not "Date": TableColumn reformats a Date column to the day only and drops the time.
const toRow = (p) => ({
  id: p.plan_id,
  pushable: !!p.pushable,
  Name: p.name || "—",
  Host: p.host?.name || p.host?.display_name || "—",
  Venue: p.venue_name || "—",
  Starts: formatWhen(p.starts_at),
  Status: p.status,
  Visibility: VISIBILITY_LABELS[p.visibility] || p.visibility,
  Guests: p.guest_count,
});

const Plans = () => {
  const navigate = useNavigate();
  const records = 20;
  const [plans, setPlans] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("active");

  // Search after typing stops, not on every key.
  useEffect(() => {
    const t = setTimeout(() => setQuery(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get(`/user-guest-lists/admin/list`, {
          params: { page: currentPage, records, name: query || undefined, status: status || undefined },
        });
        if (cancelled) return;
        setPlans(
          (response.data.data || []).map((p) => ({
            ...toRow(p),
            "Plan ID": <PlanIdCell planId={p.plan_id} />,
          }))
        );
        setTotal(response.data.total || 0);
      } catch (error) {
        console.error("Error fetching plans:", error);
        if (!cancelled) toast.error(error?.response?.data?.message || "Could not load plans");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentPage, query, status]);

  const fetchAllData = async () => {
    try {
      const response = await api.get(`/user-guest-lists/admin/list`, {
        params: { page: 1, records: 100, name: query || undefined, status: status || undefined },
      });
      return (response.data.data || []).map((p) => {
        const { pushable, ...row } = toRow(p);
        return { ...row, "Plan ID": p.plan_id };
      });
    } catch (error) {
      console.error("Error exporting plans:", error);
      return [];
    }
  };

  const columns = ["Name", "Plan ID", "Host", "Venue", "Starts", "Status", "Visibility", "Guests"];

  // Opens Manual Pushes with this plan set. Hidden where the server would refuse the push
  // (removed, deleted, unlisted or ended).
  const actions = [
    {
      hidden: (row) => !row.pushable,
      label: <MdNotificationsActive size={26} color="#4F30EA" title="Send a push for this plan" />,
      onClick: (row) => navigate(`/manual-pushes?plan_id=${encodeURIComponent(row.id)}`),
    },
  ];

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content" style={{ padding: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setCurrentPage(1);
              }}
              style={{ background: "#0e0e0e", color: "#fff", border: "1px solid #1f1f1f", borderRadius: 8, padding: "6px 10px" }}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <span style={{ color: "#777", fontSize: 13 }}>
              Copy a plan ID into Manual Pushes, or tap the bell to send a push that opens the plan.
            </span>
          </div>
          <TableColumn
            title="Plans"
            hasHeader={true}
            columns={columns}
            data={plans}
            hasExport={true}
            hasSearch={true}
            totalPages={total}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            records={records}
            actions={actions}
            inputSearchProps={{
              value: searchTerm,
              placeholder: "Search by plan or venue name",
              onChange: (e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              },
            }}
            fetchAllData={fetchAllData}
          />
        </article>
      </div>
    </main>
  );
};

export default Plans;
